import React, { FC, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { AppDispatch } from "redux/store";
import {
  setReserverBooking,
  updateBookingsDetails,
} from "redux/reducers/Payments/PaymentReducer";
import { deleteInvoice } from "redux/reducers/Properties/PropertiesSlice";
import Invoice from "containers/Booking/Invoice";
import InvoiceUser from "containers/Booking/InvoiceUser";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { currencySymbol } from "shared/CurrencySymbol";
import successHandler from "utils/helpers/SuccessHandler";
import ErrorHandler from "utils/helpers/ErrorHandler";
import { getStatusView, TONE_CLASSES } from "./BookingViews";

interface BookingRowProps {
  booking: any;
  isHost: boolean;
  onChanged?: () => Promise<void> | void;
}

const errorText = (payload: unknown, fallback: string) =>
  typeof payload === "string" && payload ? payload : fallback;

const BookingRow: FC<BookingRowProps> = ({ booking, isHost, onChanged }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [busy, setBusy] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);

  const property = booking?.property || {};
  const status: string | undefined = booking?.status;
  const view = getStatusView(status, isHost);

  const firstImage = property?.place_images?.[0];
  const image =
    typeof firstImage === "string"
      ? firstImage
      : firstImage?.url || firstImage?.image_url || null;

  const isShared = booking?.is_shared_property === true || booking?.is_shared_property === "true";
  const title =
    isShared && booking?.booked_room
      ? `${booking.booked_room} · ${property?.title || ""}`
      : property?.title || "Booking";
  const location = [property?.city, property?.state].filter(Boolean).join(", ");
  const dates = `${booking?.check_in_date || ""} to ${booking?.check_out_date || ""}`;
  const amount = `${currencySymbol()} ${(Number(booking?.total_amount) || 0).toFixed(2)}`;
  const guestName = isHost && booking?.request_by ? String(booking.request_by).split(" ")[0] : null;
  const href = `/booking_request?id=${booking?.id}`;

  const refresh = async () => {
    if (onChanged) await onChanged();
  };

  const handleCancel = async (question: string) => {
    if (!window.confirm(question)) return;
    setBusy(true);
    let ok = false;
    try {
      const action: any = await dispatch(
        updateBookingsDetails({ id: booking?.id, status: "canceled" } as any)
      );
      ok = updateBookingsDetails.fulfilled.match(action);
      if (ok) successHandler("Booking canceled.", 4000);
      else ErrorHandler(errorText(action?.payload, "Could not cancel. Try again."));
    } catch (error) {
      ErrorHandler("Could not cancel. Try again.");
    }
    setBusy(false);
    if (ok) await refresh();
  };

  const handleDeleteInvoice = async () => {
    if (!window.confirm("Delete this invoice?")) return;
    setBusy(true);
    let ok = false;
    try {
      const action: any = await dispatch(
        deleteInvoice({
          bookingId: booking?.id,
          id: booking?.invoice_no,
          _destroy: true,
          status: "canceled",
        } as any)
      );
      ok = deleteInvoice.fulfilled.match(action);
      if (ok) successHandler("Invoice deleted.", 4000);
      else ErrorHandler(errorText(action?.payload, "Could not delete the invoice."));
    } catch (error) {
      ErrorHandler("Could not delete the invoice.");
    }
    setBusy(false);
    if (ok) await refresh();
  };

  const togglePay = () => {
    dispatch(setReserverBooking(booking));
    setShowPay((prev) => !prev);
  };

  const secondaryLink =
    "text-xs text-neutral-500 hover:text-red-600 underline disabled:opacity-50";

  const renderActions = () => {
    if (isHost) {
      if (status === "booking_request") {
        return (
          <>
            <ButtonPrimary
              className="!px-4 !py-2 text-sm"
              onClick={() => setShowInvoice(true)}
            >
              Issue invoice
            </ButtonPrimary>
            <button
              type="button"
              disabled={busy}
              onClick={() => handleCancel("Reject this booking request?")}
              className="px-4 py-2 rounded-xl border border-neutral-300 dark:border-neutral-600 text-sm hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50"
            >
              Reject
            </button>
          </>
        );
      }
      if (status === "invoice_sent" && booking?.invoice_no != null) {
        return (
          <button
            type="button"
            disabled={busy}
            onClick={handleDeleteInvoice}
            className={secondaryLink}
          >
            {busy ? "Deleting..." : "Delete invoice"}
          </button>
        );
      }
      return null;
    }

    if (status === "invoice_sent") {
      return (
        <>
          <ButtonPrimary className="!px-5 !py-2 text-sm" onClick={togglePay}>
            {showPay ? "Hide" : "Pay now"}
          </ButtonPrimary>
          <button
            type="button"
            disabled={busy}
            onClick={() => handleCancel("Cancel this reservation?")}
            className={secondaryLink}
          >
            Cancel
          </button>
        </>
      );
    }
    if (status === "booking_request") {
      return (
        <button
          type="button"
          disabled={busy}
          onClick={() => handleCancel("Cancel this request?")}
          className={secondaryLink}
        >
          Cancel request
        </button>
      );
    }
    return null;
  };

  return (
    <div
      className={`rounded-2xl bg-white dark:bg-neutral-900 overflow-hidden ${
        view.needsAction
          ? "border-2 border-amber-400"
          : "border border-neutral-200 dark:border-neutral-700"
      }`}
    >
      <div className="p-3 sm:p-4 flex flex-wrap items-center gap-4">
        <Link to={href} className="flex items-center gap-4 flex-1 min-w-[240px]">
          <div className="w-24 h-20 rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-800 flex-shrink-0">
            {image && (
              <img src={image} alt="" className="w-full h-full object-cover" />
            )}
          </div>
          <div className="min-w-0">
            <p className="font-medium text-sm sm:text-base line-clamp-1">
              {guestName ? `${guestName} · ${title}` : title}
            </p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 line-clamp-1">
              {[location, dates].filter(Boolean).join(" · ")}
            </p>
            {booking?.number && (
              <p className="text-[11px] text-neutral-400 mt-0.5">
                {booking.number}
              </p>
            )}
            <span
              className={`inline-block mt-1.5 text-[11px] px-2 py-0.5 rounded-full ${TONE_CLASSES[view.tone]}`}
            >
              {view.label}
            </span>
          </div>
        </Link>

        <div className="ml-auto text-right">
          <p className="text-base font-medium mb-2">{amount}</p>
          <div className="flex items-center justify-end gap-3">
            {renderActions()}
          </div>
        </div>
      </div>

      {showPay && (
        <div className="border-t border-neutral-200 dark:border-neutral-700 p-4">
          <PayPalScriptProvider
            options={{
              clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID || "",
              currency: "USD",
              intent: "capture",
            }}
          >
            <InvoiceUser
              bookingRequestDetails={booking}
              setShowUserInvoice={setShowPay}
            />
          </PayPalScriptProvider>
        </div>
      )}

      {showInvoice && (
        <Invoice
          bookingRequestDetails={booking}
          closeInvoice={() => setShowInvoice(false)}
          callback={onChanged}
        />
      )}
    </div>
  );
};

export default BookingRow;